<template>
  <WrappedForm
    ref="form"
    class="workExperience-editor"
    labelWidth="150px"
    :model="workExperienceData"
    :formLines="formLines"
  >
    <template #salaryInfo>
      <span>{{ $t(`${i18nPrefix}.label.salaryInfo`) }}</span>
    </template>
  </WrappedForm>
</template>

<script>
import _ from 'lodash'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import moment from 'moment'

const i18nPrefix = 'admin.application.candidate'

export default {
  components: {
    WrappedForm
  },
  props: {
    workExperience: {
      required: false
    },
    jobFamilyList: {
      type: Array,
      default: null
    },
    countryList: {
      type: Array,
      default: null
    },
    provinceList: {
      type: Array,
      default: null
    },
    cityList: {
      type: Array,
      default: null
    },
    industryList: {
      type: Array,
      default: null
    },
    currencyList: {
      type: Array,
      default: null
    },
    salaryList: {
      type: Array,
      default: null
    },
    setting: {
      type: Object,
      required: false
    },
    isMiniResume: {
      type: Boolean,
      default: false
    },
    isCandidate: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix,
      workExperienceData: {}
    }
  },
  computed: {
    hiddenField () {
      if (this.isMiniResume) {
        const hiddenField = [
          'jobFamily',
          'industry',
          'country',
          'province',
          'city',
          'salary'
        ]
        if (this.setting?.hiddenField) {
          hiddenField.push(...this.setting?.hiddenField)
        }
        return hiddenField
      }
      return this.setting?.hiddenField || []
    },
    displayField () {
      return this.setting?.displayField || []
    },
    requiredField () {
      return this.setting?.requiredField || []
    },
    disabledField () {
      return this.setting?.disabledField || []
    },
    formLines () {
      const vm = this
      const requiredMessage = this.$t('common.message.requiredFormField')
      let requiredField = ['companyName', 'jobTitle', 'jobFamily', 'industry', 'country', 'province', 'city', 'startDate']
      if (this.requiredField) {
        requiredField.push(...this.requiredField)
      }
      const hiddenField = []
      if (this.hiddenField) {
        hiddenField.push(...this.hiddenField)
      }
      const displayField = []
      if (this.displayField) {
        displayField.push(...this.displayField)
      }
      const disabledField = []
      if (this.disabledField) {
        disabledField.push(...this.disabledField)
      }
      // hr not need any check
      if (!this.isCandidate) {
        requiredField = []
      }
      const lines = {
        companyName: {
          label: this.$t(`${this.i18nPrefix}.label.companyName`),
          disabled: disabledField.includes('companyName'),
          hidden: hiddenField.includes('companyName') && !displayField.includes('companyName'),
          rules: [
            { required: requiredField.includes('companyName'), message: this.$t(`${this.i18nPrefix}.message.companyNameRequired`), trigger: 'blur' }
          ]
        },
        jobTitle: {
          label: this.$t(`${this.i18nPrefix}.label.jobTitle`),
          disabled: disabledField.includes('jobTitle'),
          hidden: hiddenField.includes('jobTitle') && !displayField.includes('jobTitle'),
          rules: [{ required: requiredField.includes('jobTitle'), message: requiredMessage, trigger: 'change' }]
        },
        jobFamily: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.jobFamily`),
          disabled: disabledField.includes('jobFamily'),
          hidden: hiddenField.includes('jobFamily') && !displayField.includes('jobFamily'),
          option: {
            data: this.jobFamilyList
          },
          props: { filterable: true, popperAppendToBody: false },
          rules: [
            { required: requiredField.includes('jobFamily'), message: this.$t(`${this.i18nPrefix}.message.jobFamilyRequired`), trigger: 'blur' }
          ]
        },
        industry: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.industry`),
          disabled: disabledField.includes('industry'),
          hidden: hiddenField.includes('industry') && !displayField.includes('industry'),
          option: {
            data: this.industryList
          },
          props: { filterable: true, popperAppendToBody: false },
          rules: [
            { required: requiredField.includes('industry'), message: this.$t(`${this.i18nPrefix}.message.industryRequired`), trigger: 'blur' }
          ]
        },
        country: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.workCountry`),
          disabled: disabledField.includes('country'),
          hidden: hiddenField.includes('country') && !displayField.includes('country'),
          option: {
            data: this.countryList
          },
          props: { filterable: true, popperAppendToBody: false },
          events: { change: this.handleCountryChange },
          clearOnChange: ['province', 'city'],
          rules: [
            { required: requiredField.includes('country'), message: this.$t(`${this.i18nPrefix}.message.countryRequired`), trigger: 'blur' }
          ]
        },
        province: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.workProvince`),
          disabled: disabledField.includes('province'),
          hidden: hiddenField.includes('province') && !displayField.includes('province'),
          option: {
            data: this.provinceList
          },
          props: { filterable: true, popperAppendToBody: false },
          events: { change: this.handleProvinceChange },
          clearOnChange: ['city'],
          rules: [
            { required: requiredField.includes('province'), message: this.$t(`${this.i18nPrefix}.message.provinceRequired`), trigger: 'blur' }
          ]
        },
        city: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.workCity`),
          disabled: disabledField.includes('city'),
          hidden: hiddenField.includes('city') && !displayField.includes('city'),
          option: {
            data: this.cityList
          },
          props: { filterable: true, popperAppendToBody: false },
          rules: [
            { required: requiredField.includes('city'), message: this.$t(`${this.i18nPrefix}.message.cityRequired`), trigger: 'blur' }
          ]
        },
        startDate: {
          elType: 'el-date-picker',
          label: this.$t(`${this.i18nPrefix}.label.startDate`),
          hidden: hiddenField.includes('startDate') && !displayField.includes('startDate'),
          disabled: disabledField.includes('startDate'),
          props: {
            type: 'month',
            clearable: false,
            align: 'center',
            format: 'yyyy-MM',
            valueFormat: 'yyyy-MM',
            pickerOptions: {
              disabledDate (time) {
                return time.getTime() > Date.now()
              }
            }
          },
          rules: [{
            trigger: 'change',
            validator (rule, value, callback) {
              const endDate = vm.workExperienceData.endDate
              if (value && endDate && moment(value).isAfter(endDate, 'month')) {
                callback(vm.$t('admin.application.candidate.message.startDateAfterEndDate'))
                return
              }
              callback()
            }
          },
          {
            required: requiredField.includes('startDate'),
            message: this.$t(`${this.i18nPrefix}.message.startDateRequired`),
            trigger: 'blur'
          }
          ]
        },
        endDate: {
          elType: 'el-date-picker',
          hidden: hiddenField.includes('endDate') && !displayField.includes('endDate'),
          disabled: disabledField.includes('endDate'),
          label: this.$t(`${this.i18nPrefix}.label.endDate`),
          props: {
            type: 'month',
            clearable: false,
            align: 'center',
            format: 'yyyy-MM',
            valueFormat: 'yyyy-MM'
          },
          rules: [{
            trigger: 'change',
            validator (rule, value, callback) {
              const startDate = vm.workExperienceData.startDate
              if (value && startDate && moment(value).isBefore(startDate, 'month')) {
                callback(vm.$t('admin.application.candidate.message.startDateAfterEndDate'))
                return
              }
              callback()
            }
          },
          {
            required: requiredField.includes('endDate'),
            message: this.$t(`${this.i18nPrefix}.message.endDateRequired`),
            trigger: 'blur'
          }]
        },
        salary: {
          label: this.$t(`${this.i18nPrefix}.label.salary`),
          hidden: hiddenField.includes('salary') && !displayField.includes('salary'),
          disabled: disabledField.includes('salary'),
          class: 'startDate-form-line',
          hiddenPropName: true,
          contentExtraSlot: 'salaryInfo',
          multiple: {
            currency: {
              elType: 'el-select',
              option: {
                data: this.currencyList
              },
              props: { filterable: true, popperAppendToBody: false, clearable: true }
            },
            salary: {
              elType: 'el-select',
              option: {
                data: this.salaryList
              },
              props: { filterable: true, popperAppendToBody: false, clearable: true }
            }
          },
          rules: [
            { required: requiredField.includes('salary'), message: requiredMessage, trigger: 'blur' }
          ]
        },
        summary: {
          placeholder: this.$t(`${i18nPrefix}.label.descriptionInfo`),
          hidden: hiddenField.includes('summary') && !displayField.includes('summary'),
          disabled: disabledField.includes('summary'),
          label: this.$t(`${this.i18nPrefix}.label.description`),
          props: {
            type: 'textarea',
            autosize: { minRows: 5 }
          },
          rules: [
            { required: requiredField.includes('summary'), message: requiredMessage, trigger: 'blur' }
          ]
        }
      }
      return lines
    }
  },
  created () {

  },
  methods: {
    async getFormRef () {
      if (!this.$refs.form) {
        await this.$nextTick()
      }
      return this.$refs.form
    },
    handleCountryChange (countryId) {
      if (!countryId) {
        return
      }
      this.$emit('country-change', countryId)
    },
    handleProvinceChange (provinceId) {
      if (!provinceId) {
        return
      }
      this.$emit('province-change', provinceId)
    },

    clearData () {
      this.workExperienceData = this.toData(null)
      this.$nextTick(() => this.clearValidate())
    },
    toData (info) {
      if (!info) {
        info = {}
      }
      let jobFamily = info.jobFamily
      if (jobFamily) {
        jobFamily = parseInt(jobFamily) || null
      }
      return {
        companyName: info.companyName,
        jobTitle: info.jobTitle,
        jobFamily,
        industry: info.industry,
        country: info.country,
        province: info.province,
        city: info.city,
        startDate: info.startDate,
        endDate: info.endDate,
        currency: info.currency,
        salary: info.salary,
        summary: info.summary
      }
    },
    async clearValidate () {
      const form = await this.getFormRef()
      form.clearValidate()
    },
    async submit () {
      const form = await this.getFormRef()
      const isValid = await form.submit()
      if (!isValid) {
        return null
      } else {
        const workExperienceData = this.workExperienceData
        return {
          companyName: workExperienceData.companyName,
          jobTitle: workExperienceData.jobTitle,
          jobFamily: workExperienceData.jobFamily,
          industry: workExperienceData.industry,
          country: workExperienceData.country,
          province: workExperienceData.province,
          city: workExperienceData.city,
          startDate: workExperienceData.startDate,
          endDate: workExperienceData.endDate,
          currency: workExperienceData.currency,
          salary: workExperienceData.salary,
          summary: workExperienceData.summary
        }
      }
    }
  },
  watch: {
    workExperience: {
      handler () {
        this.workExperienceData = this.toData(this.workExperience)
        if (_.isEmpty(this.workExperienceData.provinceList)) {
          this.handleCountryChange(this.workExperienceData.country)
        }
        if (_.isEmpty(this.workExperienceData.cityList)) {
          this.handleProvinceChange(this.workExperienceData.province)
        }
        this.$nextTick(() => this.clearValidate())
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
.workExperience-editor {
  :v-deep {
    .el-select-dropdown {
      max-width: 48%;
    }

    .el-textarea {
      word-break: initial
    }
  }
}
</style>
